import { RefObject, useEffect } from 'react';

import {
  DEFAULT_AUTO_CLOSE_DELAY_MS,
  FeedbackDialogContextType,
  FeedbackType,
} from '../../components/FeedbackDialog/FeedbackDialog.types';
import { useFeedbackDialog } from '../contexts/FeedbackDialogContext';

export interface ModuleFeedbackDetail {
  message?: string;
  type?: string;
  title?: string | null;
}

type FeedbackDialogArgs = Parameters<FeedbackDialogContextType['showFeedbackDialog']>;

const feedbackTypeByModuleType = new Map<string, FeedbackType>([
  ['success', FeedbackType.SUCCESS],
  ['error', FeedbackType.ERROR],
  ['warning', FeedbackType.WARNING],
]);

const isBlank = (value: string | null | undefined): boolean =>
  value == null || value.trim().length === 0;

export const toFeedbackDialogArgs = (
  detail: ModuleFeedbackDetail | null | undefined,
): FeedbackDialogArgs | null => {
  const { message, type, title } = detail ?? {};
  if (typeof message !== 'string' || isBlank(message)) return null;
  if (title != null && typeof title !== 'string') return null;

  const feedbackType = type == null ? undefined : feedbackTypeByModuleType.get(type);
  if (feedbackType == null) return null;

  return typeof title === 'string' && !isBlank(title)
    ? [message, feedbackType, DEFAULT_AUTO_CLOSE_DELAY_MS, title]
    : [message, feedbackType, DEFAULT_AUTO_CLOSE_DELAY_MS];
};

export const useModuleFeedback = (moduleRef: RefObject<HTMLElement | null>): void => {
  const { showFeedbackDialog } = useFeedbackDialog();

  useEffect(() => {
    const element = moduleRef.current;
    if (!element) return undefined;

    const handleFeedback = (event: Event): void => {
      const args = toFeedbackDialogArgs((event as CustomEvent<ModuleFeedbackDetail>).detail);
      if (args) showFeedbackDialog(...args);
    };

    element.addEventListener('feedback', handleFeedback);

    return (): void => {
      element.removeEventListener('feedback', handleFeedback);
    };
  }, [moduleRef, showFeedbackDialog]);
};
